import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";
import StickyCallButton from "@/components/site/StickyCallButton";
import useSEO from "@/hooks/useSEO";
import { blogPostsList } from "@/data/blog";
import { SITE_URL } from "@/data/structuredData";

const BlogListPage = () => {
  useSEO({
    title: "Pittsburgh Pest Control Blog · Tips & Guides · Blue Collar World",
    description: "Practical pest control guides for Pittsburgh homeowners — termites, bed bugs, wildlife, seasonal pests. Written by licensed local pros.",
    canonical: `${SITE_URL}/blog`,
  });

  const sorted = [...blogPostsList].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <NavBar />

      <section className="border-b border-zinc-900 bg-[#F4F4F5]">
        <div className="bg-grid">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
              [ Journal ] — Field Notes
            </p>
            <h1 className="font-display uppercase font-semibold leading-[0.9] tracking-tighter text-5xl md:text-7xl mb-6">
              Pittsburgh <br />
              <span className="text-[#0B2B40]">Pest Journal.</span>
            </h1>
            <p className="text-zinc-700 text-base md:text-lg max-w-2xl">
              Real advice from licensed exterminators. No fluff, no filler, no fear-mongering. Just what you need to know about the pests in your backyard.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sorted.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group bg-white border-2 border-black p-6 flex flex-col hard-shadow-sm hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_#D9480F] transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-[#D9480F] bg-[#F4F4F5] border border-zinc-300 px-2 py-1">
                    {post.category}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.readMinutes} min
                  </span>
                </div>
                <h2 className="font-display uppercase text-xl md:text-2xl font-semibold tracking-tight leading-tight mb-3 group-hover:text-[#D9480F] transition-colors flex-1">
                  {post.title}
                </h2>
                <p className="text-zinc-700 text-sm leading-relaxed mb-5">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-zinc-300 mt-auto">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#D9480F] font-bold flex items-center gap-2 group-hover:gap-3 transition-all">
                    Read
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default BlogListPage;
